import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import PageLayout from '../../components/layout/PageLayout';
import Button from '../../components/ui/Button';
import PrinterStats from './components/PrinterStats';
import PrinterCard from './components/PrinterCard';
import AddPrinterDrawer from './components/AddPrinterDrawer';
import PrinterFilters from './components/PrinterFilters';
import DeleteConfirmModal from './components/DeleteConfirmModal';
import PrinterHistoryModal from './components/PrinterHistoryModal';
import EmptyState from './components/EmptyState';
import MaintenanceLogModal from './components/MaintenanceLogModal';
import { supabaseHelpers } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

const PrinterManagement = () => {
  // Layout agora é gerido pelo PageLayout
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingPrinter, setEditingPrinter] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ open: false, printer: null });
  const [historyModal, setHistoryModal] = useState({ open: false, printer: null });
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [statusFilter, setStatusFilter] = useState('all');
  const [brandFilter, setBrandFilter] = useState([]);
  const [operationFilter, setOperationFilter] = useState('all'); // 'all' | 'high' | 'medium' | 'low'
  const [amsFilter, setAmsFilter] = useState('all'); // 'all' | 'yes' | 'no'
  const [nozzleFilter, setNozzleFilter] = useState([]); // array de bicos selecionados
  const [modelFilter, setModelFilter] = useState([]); // array de modelos selecionados
  const [operationBasisFilter, setOperationBasisFilter] = useState('auto'); // 'auto' | 'purchase' | '30d'
  const [isLoading, setIsLoading] = useState(false);
  const [maintenanceModal, setMaintenanceModal] = useState({ open: false, printer: null });
  const { user } = useAuth();

  // Período global para cálculos por janela (UI/UX)
  const now = new Date();
  const startOfCurrentMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
  const endOfCurrentMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10);
  const [periodType, setPeriodType] = useState('current_month'); // 'current_month' | 'last_30d' | 'previous_month' | 'last_90d' | 'all_time' | 'custom'
  const [periodStart, setPeriodStart] = useState(startOfCurrentMonth);
  const [periodEnd, setPeriodEnd] = useState(endOfCurrentMonth);

  // Atualiza o intervalo de datas quando o tipo de período muda
  useEffect(() => {
    const today = new Date();
    const fmt = (d) => d.toISOString().slice(0, 10);
    if (periodType === 'current_month') {
      setPeriodStart(fmt(new Date(today.getFullYear(), today.getMonth(), 1)));
      setPeriodEnd(fmt(new Date(today.getFullYear(), today.getMonth() + 1, 0)));
    } else if (periodType === 'previous_month') {
      setPeriodStart(fmt(new Date(today.getFullYear(), today.getMonth() - 1, 1)));
      setPeriodEnd(fmt(new Date(today.getFullYear(), today.getMonth(), 0)));
    } else if (periodType === 'last_30d') {
      const start = new Date(today);
      start.setDate(start.getDate() - 29);
      setPeriodStart(fmt(start));
      setPeriodEnd(fmt(today));
    } else if (periodType === 'last_90d') {
      const start = new Date(today);
      start.setDate(start.getDate() - 89);
      setPeriodStart(fmt(start));
      setPeriodEnd(fmt(today));
    } else if (periodType === 'all_time') {
      setPeriodStart(null);
      setPeriodEnd(null);
    }
    // 'custom' mantém os valores definidos manualmente
  }, [periodType]);

  const [printers, setPrinters] = useState([]);

  // Carregar impressoras do backend (Supabase ou API local)
  useEffect(() => {
    let mounted = true;
    const loadPrinters = async () => {
      if (!user?.id) return;
      setIsLoading(true);
      try {
        const { data, error } = await supabaseHelpers.getUserPrinters(user?.id);
        if (error) {
          console.error('Erro ao carregar impressoras:', error);
        }
        if (mounted) {
          const list = Array.isArray(data) ? data : [];
          // Normalizar alguns campos para compatibilidade com UI
          const normalized = list.map(p => ({
            ...p,
            // Imagens
            image: p?.image_url || p?.image,
            image_urls: Array.isArray(p?.image_urls) ? p?.image_urls : [],
            // Marca e modelo
            brand: p?.brand || p?.manufacturerRef?.name || p?.manufacturer,
            model: p?.model || p?.modelRef?.name,
            // Especificações técnicas
            nozzleSize: p?.nozzleSize || p?.nozzle_size || p?.modelRef?.nozzle_diameter,
            build_volume_x: p?.build_volume_x ?? p?.modelRef?.build_volume_x ?? null,
            build_volume_y: p?.build_volume_y ?? p?.modelRef?.build_volume_y ?? null,
            build_volume_z: p?.build_volume_z ?? p?.modelRef?.build_volume_z ?? null,
            // Estado/uso
            hasAMS: p?.hasAMS ?? p?.has_ams ?? false,
            status: p?.status || 'active',
            // Custos e fornecedor
            powerConsumption: p?.power_consumption ?? p?.powerConsumption ?? null,
            purchasePrice: p?.purchase_price ?? p?.purchasePrice ?? null,
            purchaseDate: p?.purchase_date ?? p?.purchaseDate ?? null,
            supplier: p?.supplier ?? null
          }));
          setPrinters(normalized);
        }
      } catch (err) {
        console.error('Falha ao carregar impressoras:', err);
      } finally {
        if (mounted) setIsLoading(false);
      }
    };
    loadPrinters();
    return () => { mounted = false; };
  }, [user?.id]);

  // Debounced search
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Filtered and sorted printers
  const filteredPrinters = useMemo(() => {
    const getOperationPercent = (p) => {
      // calcula % de dias em operação igual ao card
      const logs = Array.isArray(p?.maintenanceLogs) ? p.maintenanceLogs : [];
      const daysDownTotal = logs?.reduce((sum, log) => sum + (Number(log?.daysDown || 0)), 0);
      const daysDownLast30 = logs?.reduce((sum, log) => {
        const d = new Date(log?.date);
        if (isNaN(d)) return sum;
        const threshold = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
        return d >= threshold ? sum + (Number(log?.daysDown || 0)) : sum;
      }, 0);
      const d = new Date(p?.purchaseDate);
      const hasPurchase = !isNaN(d);
      const basis = operationBasisFilter === 'purchase'
        ? (hasPurchase ? Math.max(1, Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24))) : 1)
        : operationBasisFilter === '30d'
          ? 30
          : (hasPurchase ? Math.max(1, Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24))) : 30);
      const down = (operationBasisFilter === 'purchase' || (operationBasisFilter === 'auto' && hasPurchase))
        ? Number(daysDownTotal || 0)
        : Number(daysDownLast30 || 0);
      const opDays = Math.max(0, basis - down);
      return Math.max(0, Math.min(100, (opDays / basis) * 100));
    };

    let filtered = printers?.filter((printer) => {
      const matchesSearch = !debouncedSearchTerm ||
      printer?.name?.toLowerCase()?.includes(debouncedSearchTerm?.toLowerCase()) ||
      printer?.model?.toLowerCase()?.includes(debouncedSearchTerm?.toLowerCase()) ||
      printer?.manufacturer?.toLowerCase()?.includes(debouncedSearchTerm?.toLowerCase());

      const matchesStatus = statusFilter === 'all' || printer?.status === statusFilter;

      const brandValue = printer?.brand || printer?.manufacturer;
      const matchesBrand = !brandFilter || brandFilter?.length === 0 || brandFilter?.includes(brandValue);

      const opPct = getOperationPercent(printer);
      const matchesOperation = (() => {
        if (operationFilter === 'all') return true;
        if (operationFilter === 'high') return opPct > 66;
        if (operationFilter === 'medium') return opPct > 33 && opPct <= 66;
        if (operationFilter === 'low') return opPct <= 33;
        return true;
      })();

      const matchesAMS = amsFilter === 'all' || (amsFilter === 'yes' ? !!printer?.hasAMS : amsFilter === 'no' ? !printer?.hasAMS : true);

      const nozzleValue = printer?.nozzleSize ? String(printer?.nozzleSize) : '';
      const matchesNozzle = !nozzleFilter || nozzleFilter?.length === 0 || nozzleFilter?.includes(nozzleValue);

      const matchesModel = !modelFilter || modelFilter?.length === 0 || modelFilter?.includes(printer?.model);

      return matchesSearch && matchesStatus && matchesBrand && matchesOperation && matchesAMS && matchesNozzle && matchesModel;
    });

    // Sort printers
    const parseDateSafe = (val) => {
      if (!val) return 0;
      const d = new Date(val);
      return isNaN(d) ? 0 : d.getTime();
    };
    const parseIdNumeric = (val) => {
      const n = Number(val);
      return isNaN(n) ? 0 : n;
    };
    filtered?.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a?.name?.localeCompare(b?.name);
        case 'name-desc':
          return b?.name?.localeCompare(a?.name);
        case 'hours':
          return b?.totalHours - a?.totalHours;
        case 'hours-asc':
          return a?.totalHours - b?.totalHours;
        case 'recent':
          // Preferir created_at quando disponível; senão, tentar id numérico
          const bCreated = parseDateSafe(b?.created_at);
          const aCreated = parseDateSafe(a?.created_at);
          if (bCreated !== aCreated) return bCreated - aCreated;
          return parseIdNumeric(b?.id) - parseIdNumeric(a?.id);
        case 'oldest':
          const bCreated2 = parseDateSafe(b?.created_at);
          const aCreated2 = parseDateSafe(a?.created_at);
          if (bCreated2 !== aCreated2) return aCreated2 - bCreated2;
          return parseIdNumeric(a?.id) - parseIdNumeric(b?.id);
        default:
          return 0;
      }
    });

    return filtered;
  }, [printers, debouncedSearchTerm, sortBy, statusFilter, brandFilter, operationFilter, amsFilter, nozzleFilter, modelFilter, operationBasisFilter]);

  // Total de horas para calcular participação de uso relativa no conjunto exibido
  // Exclui impressoras marcadas como "Inexistente" do denominador
  const totalHoursAll = useMemo(() => {
    const list = Array.isArray(filteredPrinters) ? filteredPrinters : printers;
    const eligible = list?.filter(p => p?.status !== 'nonexistent') || [];
    return eligible?.reduce((sum, p) => sum + (Number(p?.totalHours || 0)), 0) || 0;
  }, [filteredPrinters, printers]);

  // Participação de uso no período selecionado (ou vida toda quando não há período válido)
  const usageShareByPrinter = useMemo(() => {
    const list = Array.isArray(filteredPrinters) ? filteredPrinters : printers;
    const eligible = list?.filter(p => p?.status !== 'nonexistent') || [];

    const start = periodStart ? new Date(periodStart) : null;
    const endRaw = periodEnd ? new Date(periodEnd) : null;
    const end = endRaw ? new Date(endRaw.getTime()) : null;
    if (end) end.setHours(23, 59, 59, 999);
    const periodActive = Boolean(start && end && !isNaN(start) && !isNaN(end));

    const parseDate = (val) => {
      if (!val) return null;
      const d = new Date(val);
      return isNaN(d) ? null : d;
    };
    const getJobDate = (j) => parseDate(j?.completed_at) || parseDate(j?.created_at) || parseDate(j?.endTime) || parseDate(j?.startTime) || parseDate(j?.usage_date) || parseDate(j?.date);
    const jobHours = (j) => Number(j?.duration_hours || j?.estimated_duration_hours || j?.durationHours || j?.hours || 0);
    const isWithin = (d) => {
      if (!periodActive) return true;
      if (!d) return false;
      return d >= start && d <= end;
    };

    const hoursByPrinter = {};
    eligible?.forEach(p => {
      const jobs = Array.isArray(p?.jobs) ? p?.jobs : [];
      const hours = periodActive
        ? (jobs?.reduce((sum, j) => {
            const d = getJobDate(j);
            return isWithin(d) ? sum + jobHours(j) : sum;
          }, 0) || 0)
        : Number(p?.totalHours || 0);
      hoursByPrinter[p?.id] = hours;
    });

    const total = eligible?.reduce((sum, p) => sum + (hoursByPrinter[p?.id] || 0), 0) || 0;
    const result = {};
    eligible?.forEach(p => {
      const pct = total > 0 ? Math.max(0, Math.min(100, ((hoursByPrinter[p?.id] || 0) / total) * 100)) : 0;
      result[p?.id] = pct;
    });

    return result;
  }, [filteredPrinters, printers, periodStart, periodEnd]);

  const handleAddPrinter = () => {
    setEditingPrinter(null);
    setDrawerOpen(true);
  };

  const handleEditPrinter = (printer) => {
    setEditingPrinter(printer);
    setDrawerOpen(true);
  };

  const handleSavePrinter = async (printerData) => {
    setIsLoading(true);
    const normalizeStatus = (s) => {
      if (s === 'idle') return 'active';
      if (s === 'nonexistent') return 'inactive';
      return s || 'active';
    };
    const isUuid = (v) => typeof v === 'string' && /[0-9a-fA-F\-]{32,}/.test(v);
    const payload = {
      user_id: user?.id,
      name: String(printerData?.name || '').trim() || 'Sem nome',
      brand: (() => {
        const s = String(printerData?.brand || '').trim();
        return s || 'Personalizado';
      })(),
      model: (() => {
        const s = String(printerData?.model || '').trim();
        return s || 'Desconhecido';
      })(),
      image_url: Array.isArray(printerData?.image_urls) ? (printerData?.image_urls?.[0] || null) : (printerData?.image || null),
      status: normalizeStatus(printerData?.status),
      hourly_rate: (() => { const n = parseFloat(printerData?.hourly_rate); return isNaN(n) ? null : n; })(),
      power_consumption: (() => { const n = parseFloat(printerData?.power_consumption); return isNaN(n) ? null : n; })(),
      build_volume_x: (() => { const n = parseInt(printerData?.build_volume_x); return isNaN(n) ? null : n; })(),
      build_volume_y: (() => { const n = parseInt(printerData?.build_volume_y); return isNaN(n) ? null : n; })(),
      build_volume_z: (() => { const n = parseInt(printerData?.build_volume_z); return isNaN(n) ? null : n; })(),
      notes: printerData?.notes || null,
      manufacturer_id: isUuid(printerData?.manufacturer_id) ? printerData?.manufacturer_id : null,
      model_id: isUuid(printerData?.model_id) ? printerData?.model_id : null,
      // Campos adicionais para persistência completa
      has_ams: Boolean(printerData?.hasAMS),
      nozzle_size: (() => {
        const v = printerData?.nozzleSize;
        if (v === '' || v == null) return null;
        const num = parseFloat(String(v).replace(',', '.'));
        return isNaN(num) ? String(v) : num;
      })(),
      supplier: printerData?.supplier || null,
      purchase_price: (() => {
        const n = parseFloat(printerData?.purchasePrice);
        return isNaN(n) ? null : n;
      })(),
      purchase_date: printerData?.purchaseDate || null
    };

    try {
      if (editingPrinter) {
        const { data, error } = await supabaseHelpers.updatePrinter(editingPrinter?.id, payload);
        if (error) {
          console.error('Erro ao atualizar impressora:', error);
        }
        const updated = {
          ...editingPrinter,
          ...printerData,
          ...payload,
          id: data?.id || editingPrinter?.id,
          image: payload?.image_url || printerData?.image,
          status: payload?.status
        };
        setPrinters((prev) => prev?.map((p) => p?.id === editingPrinter?.id ? updated : p));
      } else {
        const { data, error } = await supabaseHelpers.createPrinter(payload);
        if (error) {
          console.error('Erro ao criar impressora:', error);
        }
        const created = {
          ...printerData,
          ...payload,
          id: data?.id || printerData?.id,
          image: payload?.image_url || printerData?.image,
          status: payload?.status
        };
        setPrinters((prev) => [...prev, created]);
      }
    } catch (err) {
      console.error('Falha no salvamento da impressora:', err);
    } finally {
      setIsLoading(false);
      setDrawerOpen(false);
      setEditingPrinter(null);
    }
  };

  const handleDeletePrinter = (printer) => {
    setDeleteModal({ open: true, printer });
  };

  const confirmDeletePrinter = async () => {
    setIsLoading(true);
    try {
      const printerId = deleteModal?.printer?.id;
      const { error } = await supabaseHelpers.deletePrinter(printerId);
      if (error) {
        console.error('Erro ao excluir impressora:', error);
      }
      setPrinters((prev) => prev?.filter((p) => p?.id !== printerId));
      setDeleteModal({ open: false, printer: null });
    } catch (err) {
      console.error('Falha ao excluir impressora:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewHistory = (printer) => {
    setHistoryModal({ open: true, printer });
  };

  const handleOpenMaintenanceLog = (printer) => {
    setMaintenanceModal({ open: true, printer });
  };

  const handleSaveMaintenanceLog = async (log, printer) => {
    // Simule API e atualize estado
    setPrinters(prev => prev?.map(p => {
      if (p?.id !== printer?.id) return p;
      const existingLogs = Array.isArray(p?.maintenanceLogs) ? p.maintenanceLogs : [];
      const updated = { ...p, maintenanceLogs: [...existingLogs, { ...log, id: Date.now().toString() }] };
      // ao concluir manutenção, retorna para idle
      if (p?.status === 'maintenance') {
        updated.status = 'idle';
      }
      return updated;
    }));
    // Persistir status como 'active' ao concluir manutenção (Supabase)
    try {
      await supabaseHelpers.updatePrinterStatus(printer?.id, 'active', 'Manutenção concluída');
    } catch (err) {
      console.error('Falha ao atualizar status pós-manutenção:', err);
    }
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setSortBy('name');
    setStatusFilter('all');
    setBrandFilter([]);
    setModelFilter([]);
    setOperationFilter('all');
    setAmsFilter('all');
    setNozzleFilter([]);
    setOperationBasisFilter('auto');
  };

  const hasActiveFilters = searchTerm || sortBy !== 'name' || statusFilter !== 'all' || (brandFilter?.length > 0);
  const hasNoResults = filteredPrinters?.length === 0;

  const availableBrands = useMemo(() => {
    const values = printers?.map(p => p?.brand || p?.manufacturer)?.filter(Boolean) || [];
    return Array.from(new Set(values))?.sort((a, b) => a?.localeCompare(b));
  }, [printers]);

  const availableNozzles = useMemo(() => {
    const values = printers?.map(p => p?.nozzleSize)?.filter(Boolean) || [];
    return Array.from(new Set(values));
  }, [printers]);

  const availableModelsByBrand = useMemo(() => {
    const map = {};
    printers?.forEach(p => {
      const brand = p?.brand || p?.manufacturer;
      const model = p?.model;
      if (!brand || !model) return;
      if (!map[brand]) map[brand] = new Set();
      map[brand].add(model);
    });
    // convert Set to array
    const obj = {};
    Object.keys(map)?.forEach(b => { obj[b] = Array.from(map[b]); });
    return obj;
  }, [printers]);

  const availableModels = useMemo(() => {
    const selectedBrands = Array.isArray(brandFilter) ? brandFilter : [];
    if (selectedBrands?.length > 0) {
      let models = [];
      selectedBrands?.forEach(b => { models = models.concat(availableModelsByBrand?.[b] || []); });
      return Array.from(new Set(models))?.sort((a, b) => a?.localeCompare(b));
    }
    const all = printers?.map(p => p?.model)?.filter(Boolean) || [];
    return Array.from(new Set(all))?.sort((a, b) => a?.localeCompare(b));
  }, [printers, brandFilter, availableModelsByBrand]);

  // Mapa de modelos -> marcas para autofill de marca ao selecionar modelo
  const modelToBrands = useMemo(() => {
    const map = {};
    printers?.forEach(p => {
      const brand = p?.brand || p?.manufacturer;
      const model = p?.model;
      if (!model || !brand) return;
      if (!map[model]) map[model] = new Set();
      map[model].add(brand);
    });
    const obj = {};
    Object.keys(map)?.forEach(m => { obj[m] = Array.from(map[m]); });
    return obj;
  }, [printers]);

  // Ao filtrar por modelo, preencher automaticamente as marcas correspondentes
  const handleModelFilterChange = (nextModels) => {
    const models = Array.isArray(nextModels) ? nextModels : [];
    setModelFilter(models);
    if (models.length === 0) {
      setBrandFilter([]);
      return;
    }
    const brandSet = new Set();
    models.forEach(m => {
      (modelToBrands?.[m] || [])?.forEach(b => brandSet.add(b));
    });
    setBrandFilter(Array.from(brandSet));
  };

  return (
    <>
      <Helmet>
        <title>Gerenciar Impressoras - Moskorz</title>
        <meta name="description" content="Gerencie sua coleção de impressoras 3D, acompanhe uso e histórico de impressões" />
      </Helmet>
      <PageLayout
        title="Gerenciar Impressoras"
        subtitle="Controle sua coleção de impressoras 3D e acompanhe o desempenho"
        iconName="Printer"
        headerRight={(
          <Button
            iconName="Plus"
            iconPosition="left"
            onClick={handleAddPrinter}
            className="hover-lift lg:w-auto sm:w-full">
            Nova Impressora
          </Button>
        )}
      >
        {/* Stats Section */}
        <PrinterStats printers={filteredPrinters} />

        {/* Filters Section */}
              <PrinterFilters
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                sortBy={sortBy}
                onSortChange={setSortBy}
                statusFilter={statusFilter}
                onStatusFilterChange={setStatusFilter}
                brandFilter={brandFilter}
                onBrandFilterChange={setBrandFilter}
                availableBrands={availableBrands}
                modelFilter={modelFilter}
                onModelFilterChange={handleModelFilterChange}
                availableModels={availableModels}
                operationFilter={operationFilter}
                onOperationFilterChange={setOperationFilter}
                amsFilter={amsFilter}
                onAmsFilterChange={setAmsFilter}
                nozzleFilter={nozzleFilter}
                onNozzleFilterChange={setNozzleFilter}
                availableNozzles={availableNozzles}
                operationBasisFilter={operationBasisFilter}
                onOperationBasisFilterChange={setOperationBasisFilter}
                periodType={periodType}
                onPeriodTypeChange={setPeriodType}
                periodStart={periodStart}
                periodEnd={periodEnd}
                onPeriodStartChange={setPeriodStart}
                onPeriodEndChange={setPeriodEnd}
                onClearFilters={handleClearFilters}
                totalCount={printers?.length || 0}
                filteredCount={filteredPrinters?.length || 0} />

        {/* Removido: Toggle de layout. Mantemos apenas a grade. */}


        {/* Grid/List Section */}
        {hasNoResults ? (
          <EmptyState
            onAddPrinter={handleAddPrinter}
            hasFilters={hasActiveFilters}
            onClearFilters={handleClearFilters}
          />
        ) : (
          (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredPrinters?.map((printer) => (
            <PrinterCard
              key={printer?.id}
              printer={printer}
              periodType={periodType}
              periodStart={periodStart}
              periodEnd={periodEnd}
              onPeriodTypeChange={setPeriodType}
              usageSharePct={usageShareByPrinter?.[printer?.id] || 0}
              onEdit={handleEditPrinter}
              onDelete={handleDeletePrinter}
              onViewHistory={handleViewHistory}
              onAddMaintenanceLog={handleOpenMaintenanceLog}
              onStatusChange={async (p, newStatus) => {
                const normalizeStatus = (s) => {
                  if (s === 'idle') return 'active';
                  if (s === 'nonexistent') return 'inactive';
                  return s || 'active';
                };
                const mapped = normalizeStatus(newStatus);
                // Atualiza UI imediatamente
                setPrinters(prev => prev?.map(item => item?.id === p?.id ? { ...item, status: newStatus } : item));
                // Persiste no backend
                try {
                  const { error } = await supabaseHelpers.updatePrinterStatus(p?.id, mapped);
                  if (error) console.error('Erro ao atualizar status da impressora:', error);
                } catch (err) {
                  console.error('Falha ao atualizar status da impressora:', err);
                }
              }}
            />
          ))}
            </div>
          )
        )}
        </PageLayout>

        {/* Modals and Drawers */}
        <AddPrinterDrawer
          isOpen={drawerOpen}
          onClose={() => {
            setDrawerOpen(false);
            setEditingPrinter(null);
          }}
          onSave={handleSavePrinter}
          editingPrinter={editingPrinter} />


        <DeleteConfirmModal
          isOpen={deleteModal?.open}
          onClose={() => setDeleteModal({ open: false, printer: null })}
          onConfirm={confirmDeletePrinter}
          printerName={deleteModal?.printer?.name}
          isLoading={isLoading} />


        <PrinterHistoryModal
          isOpen={historyModal?.open}
          onClose={() => setHistoryModal({ open: false, printer: null })}
          printer={historyModal?.printer} />

        <MaintenanceLogModal
          isOpen={maintenanceModal?.open}
          onClose={() => setMaintenanceModal({ open: false, printer: null })}
          onSave={handleSaveMaintenanceLog}
          printer={maintenanceModal?.printer}
        />

    </>);

};

export default PrinterManagement;